import React, { useState, useEffect } from 'react'
import type { Venue } from '../types'
import type { BrandAd } from './SponsoredBanner'
import {
  listWeeklyFeatures, saveWeeklyFeature, deleteWeeklyFeature,
  mondayOf, type WeeklyFeature,
} from '../services/weeklyFeatureService'

/**
 * Admin manager for Happy Hour of the Week.
 * Schedule weeks ahead: each row is one week (Monday-keyed, UNIQUE in DB),
 * shows automatically during its week, expires automatically after.
 * Sponsor dropdown lists brand_ads rows — create the sponsor as a brand ad
 * first (it can stay inactive so it doesn't enter banner rotation).
 */
export function WeeklyFeatureManager({ venues, brandAds }: { venues: Venue[]; brandAds: BrandAd[] }) {
  const [features, setFeatures] = useState<WeeklyFeature[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // form state
  const [venueId, setVenueId] = useState('')
  const [sponsorId, setSponsorId] = useState('')
  const [weekStart, setWeekStart] = useState(() => {
    const nextMonday = new Date()
    nextMonday.setDate(nextMonday.getDate() + 7)
    return mondayOf(nextMonday)
  })
  const [headline, setHeadline] = useState('')

  async function load() {
    setLoading(true)
    setErr(null)
    try { setFeatures(await listWeeklyFeatures()) }
    catch (e: any) { setErr(e.message) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  async function handleSave() {
    if (!venueId) { setErr('Pick a venue'); return }
    setSaving(true)
    setErr(null)
    try {
      await saveWeeklyFeature({
        venue_id: venueId,
        sponsor_ad_id: sponsorId || null,
        week_start: mondayOf(new Date(weekStart + 'T00:00:00')),  // normalize to Monday
        headline,
      })
      setVenueId(''); setSponsorId(''); setHeadline('')
      await load()
    } catch (e: any) {
      setErr(e.message.includes('duplicate') || e.message.includes('unique')
        ? 'That week already has a feature — delete it first.'
        : e.message)
    }
    setSaving(false)
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Remove this weekly feature?')) return
    try { await deleteWeeklyFeature(id); await load() }
    catch (e: any) { setErr(e.message) }
  }

  const venueName = (id: string) => venues.find(v => v.id === id)?.name ?? '(deleted venue)'
  const sponsorName = (id: string | null) =>
    id ? (brandAds.find(a => a.id === id)?.brand_name ?? '(deleted sponsor)') : null
  const thisMonday = mondayOf(new Date())

  const input = { padding: '8px 10px', border: '1px solid #EAE6DF', borderRadius: 8, fontSize: 13, background: '#fff' } as const

  return (
    <div>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>🏆 Happy Hour of the Week</h3>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#888' }}>
        One venue per week, Monday–Sunday. Shows on Browse automatically during its week, disappears after.
        Sponsor is optional — to add one, create the brand under Brand Ads first (leave it inactive).
      </p>

      {err && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

      {/* Schedule form */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', background: '#fff', border: '1px solid #EAE6DF', borderRadius: 12, padding: 14, marginBottom: 20 }}>
        <label style={{ fontSize: 11, color: '#888' }}>Venue<br />
          <select value={venueId} onChange={e => setVenueId(e.target.value)} style={{ ...input, minWidth: 200 }}>
            <option value="">— pick venue —</option>
            {[...venues].sort((a, b) => a.name.localeCompare(b.name)).map(v =>
              <option key={v.id} value={v.id}>{v.name} ({v.neighborhood})</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11, color: '#888' }}>Week of (snaps to Monday)<br />
          <input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} style={input} />
        </label>
        <label style={{ fontSize: 11, color: '#888' }}>Sponsor (optional)<br />
          <select value={sponsorId} onChange={e => setSponsorId(e.target.value)} style={{ ...input, minWidth: 160 }}>
            <option value="">— none —</option>
            {brandAds.map(a => <option key={a.id} value={a.id}>{a.brand_name}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 11, color: '#888', flex: 1, minWidth: 180 }}>Blurb (optional, e.g. "Best patio deal in OTR")<br />
          <input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={80} style={{ ...input, width: '100%' }} />
        </label>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '9px 18px', background: '#E85D1A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: saving ? .6 : 1 }}>
          {saving ? 'Saving…' : 'Schedule week'}
        </button>
      </div>

      {/* Scheduled list */}
      {loading ? (
        <div style={{ color: '#888', fontSize: 13 }}>Loading…</div>
      ) : features.length === 0 ? (
        <div style={{ color: '#888', fontSize: 13 }}>Nothing scheduled yet.</div>
      ) : (
        features.map(f => {
          const isCurrent = f.week_start === thisMonday
          const isPast = f.week_start < thisMonday
          return (
            <div key={f.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: '#fff', border: `1px solid ${isCurrent ? '#E85D1A' : '#EAE6DF'}`,
              borderRadius: 10, marginBottom: 8, opacity: isPast ? .55 : 1,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, minWidth: 90 }}>
                {f.week_start}
                {isCurrent && <span style={{ display: 'block', color: '#E85D1A', fontSize: 10 }}>LIVE NOW</span>}
                {isPast && <span style={{ display: 'block', color: '#aaa', fontSize: 10 }}>past</span>}
              </div>
              <div style={{ flex: 1, fontSize: 13 }}>
                <strong>{venueName(f.venue_id)}</strong>
                {sponsorName(f.sponsor_ad_id) && (
                  <span style={{ color: '#8B5CF6', fontSize: 11, marginLeft: 8 }}>
                    · presented by {sponsorName(f.sponsor_ad_id)}
                  </span>
                )}
                {f.headline && <div style={{ fontSize: 11, color: '#888' }}>{f.headline}</div>}
              </div>
              <button onClick={() => handleDelete(f.id)}
                style={{ background: 'none', border: '1px solid #EAE6DF', borderRadius: 8, padding: '5px 10px', fontSize: 11, color: '#991B1B', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          )
        })
      )}
    </div>
  )
}
